import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DashboardProviderProd } from '@models/dashboard-providers.model';
import { DashboardMeasureDetailProd } from '@models/dashboard-measure-details.model';
import * as fromDashboard from '../../reducers/dashboard/dashboard.reducer';

export const selectDashboardState = createFeatureSelector<fromDashboard.State>(
  fromDashboard.dashboardFeatureKey
);

export const selectProviders = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.providers
    }
);

export const selectActiveProviders = createSelector(
  selectProviders,
  (providers) => providers.filter((provider) => provider.active == 1)
)

export const selectLocations = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.locations
    }
);

export const selectSortedProviders = createSelector(
    selectActiveProviders,
    (providers) => [...providers].sort((a: DashboardProviderProd, b: DashboardProviderProd) => {
            const lastNameA = a.lastName?.toLowerCase() || '';
            const lastNameB = b.lastName?.toLowerCase() || '';
            const firstNameA = a.firstName?.toLowerCase() || '';
            const firstNameB = b.firstName?.toLowerCase() || '';

            if (lastNameA < lastNameB) return -1;
            if (lastNameA > lastNameB) return 1;
            if (firstNameA < firstNameB) return -1;
            if (firstNameA > firstNameB) return 1;
            return 0;
          })
);

export const selectDetailsTab = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.selectedDetailsTab
    }
);

export const selectMeasure = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.selectedMeasure
    }
);

export const selectIndex = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.selectedIndex
    }
);

export const selectShowMeasureDetails = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.showMeasureDetails
    }
);

export const selectMeasures = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.measures
    }
);

function getBaseMeasureCode(measureName: string): string {
    const nameWithoutYearSuffix = measureName.replace(/_\d{4}$/, "");
    if (/^[a-zA-Z]+-\d+-\d+$/.test(nameWithoutYearSuffix)) {
        return nameWithoutYearSuffix.replace(/-\d+$/, "");
    }
    return nameWithoutYearSuffix;
}

export interface ImpactedMeasureOption {
    code: string;
    title: string;
}

export const selectImpactedMeasureOptions = createSelector(
    selectMeasures,
    (measures) => {
        const measureByName = new Map<string, DashboardMeasureDetailProd>();
        measures.forEach((measure) => {
            if (measure.name) measureByName.set(measure.name, measure);
        });

        const optionsByCode = new Map<string, ImpactedMeasureOption>();
        measures.forEach((measure) => {
            if (!measure.name) return;
            const baseCode = getBaseMeasureCode(measure.name);
            if (!optionsByCode.has(baseCode)) {
                const baseMeasure = measureByName.get(baseCode) || measure;
                optionsByCode.set(baseCode, {
                    code: baseCode,
                    title: baseMeasure.measureDisplayName || baseCode,
                });
            }
        });
        return Array.from(optionsByCode.values());
    }
);

export const selectFilteredMeasures = createSelector(
    selectMeasures,
   (measures) => measures.filter((measure) => {
    if (measure.isMultiPerformance == null || measure.isMultiPerformance == undefined || measure.isMultiPerformance === false) {
        return true;
    }
    if (measure.isMultiPerformance === true) {
      if (measure.showChild === true) {
        return true;
      }

      const baseName = measure.name?.split('-')[0] ?? '';
      const matchingMeasure = measures.find(m => m.name?.startsWith(baseName) && m.name !== measure.name && m.expanded === true);
      if (matchingMeasure) {
        return true;
      }

      return false;
    }

    return true;

   })
);

export const selectMonthlyTrend = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.monthlyTrend
    }
);

export const selectIsMonthlyTrendLoading = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => state.isMonthlyTrendLoading
);

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export const selectMonthlyTrendChartData = createSelector(
    selectMonthlyTrend,
    (monthlyTrend) => {
        const seen = new Set<string>();
        return [...monthlyTrend]
            .filter(item => {
                const key = `${item.calcYear}-${item.calcMonth}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .sort((a, b) => a.calcYear !== b.calcYear ? a.calcYear - b.calcYear : a.calcMonth - b.calcMonth)
            .map(item => ({
                ...item,
                monthLabel: `${MONTH_NAMES[item.calcMonth - 1]} ${item.calcYear}`,
                ratePercent: (item.performance != null && item.denominator !== 0) ? Math.round(item.performance * 10000) / 100 : null
            }));
    }
);

export const selectMonthlyTrendChartOptions = createSelector(
    selectMonthlyTrendChartData,
    (chartData) => {
        return {
            title: { text: "" },
            axes: [
                {
                    position: "bottom",
                    type: "category",
                    title: { text: "Month" },
                    gridLine: { enabled: true },
                    label: { rotation: -45 }
                },
                {
                    position: "left",
                    type: "number",
                    title: { text: "Performance Rate (%)" },
                    gridLine: { enabled: false },
                    label: {
                        formatter: (params: any) => `${params.value}%`
                    }
                }
            ],
            series: [{
                type: "line",
                xKey: "monthLabel",
                xName: "Month",
                yKey: "ratePercent",
                yName: "Performance Rate",
                data: chartData,
                tooltip: {
                    renderer: (params: any) => {
                        const rate = params.datum.ratePercent != null
                            ? `${params.datum.ratePercent.toFixed(1)}%`
                            : 'N/A';
                        return `
                            <div class="ag-chart-tooltip-title">${params.datum.monthLabel}</div>
                            <div class="ag-chart-tooltip-content">
                                <div>Numerator: ${params.datum.numerator}</div>
                                <div>Denominator: ${params.datum.denominator}</div>
                                <div>Rate: ${rate}</div>
                            </div>
                        `;
                    }
                }
            }],
            legend: { enabled: false }
        };
    }
);

export const selectMonthlyTrendTableRows = createSelector(
    selectMonthlyTrendChartData,
    (chartData) => chartData.map(item => ({
        monthLabel: item.monthLabel,
        numerator: item.numerator,
        denominator: item.denominator,
        rateDisplay: item.ratePercent != null ? `${item.ratePercent.toFixed(1)}%` : 'N/A'
    }))
);

export const selectSelectedMeasureCode = createSelector(
    selectDashboardState,
    (state: fromDashboard.State) => {
        return state.selectedMeasure?.name
    }
);

export const selectSelectedMonthlyTrendYears = createSelector(
  selectDashboardState,
  (state: fromDashboard.State) => {
    return state.selectedMonthlyTrendYears
  }
)

export const selectSelectedMonthlyTrendYearsForm = createSelector(
  selectDashboardState,
    (state: fromDashboard.State) => ({
        years: state.selectedMonthlyTrendYears
    })
);

export const selectMipsScores = createSelector(
  selectDashboardState,
  (state) => state.mipsScores
);

export const selectMvpScores = createSelector(
  selectDashboardState,
  (state) => state.mvpScores
);

export const selectMipsRequirements = createSelector(
  selectDashboardState,
  (state) => state.mipsRequirements
);

export const selectTopTraditionalMeasures = createSelector(
  selectDashboardState,
  (state) => state.topTraditionalMeasures
);

export const selectTopMvpMeasures = createSelector(
  selectDashboardState,
  (state) => state.topMvpMeasures
);

export const selectMipsScoresLoading = createSelector(
  selectDashboardState,
  (state) => state.mipsScoresLoading
);

export const selectIsSmallPractice = createSelector(
  selectDashboardState,
  (state) => state.isSmallPractice
);

export const selectMVPMeasures = createSelector(
  selectMeasures,
  (measures) => measures.filter(m => m.isMVP === true)
);

export const selectTraditionalMeasures = createSelector(
  selectMeasures,
  (measures) => measures.filter(m => m.isMIPS === true)
);

export const selectProgramYear = createSelector(
  selectDashboardState,
  (state) => state.programYear
);

export const selectFavoriteMeasureCodes = createSelector(
  selectDashboardState,
  (state) => state.favoriteMeasureCodes
);

export const selectIsMultiOrgTin = createSelector(selectDashboardState, (state) => state.isMultiOrgTin);
export const selectSavedFilters = createSelector(selectDashboardState, (state) => state.savedFilters);
export const selectDeciles = createSelector(selectDashboardState, (state) => state.deciles);
export const selectDecilesYear = createSelector(selectDashboardState, (state) => state.decilesYear);
export const selectMipsReweighting = createSelector(selectDashboardState, (state) => state.reweighting);
export const selectMipsReweightingLoading = createSelector(selectDashboardState, (state) => state.reweightingLoading);

// Combines the four mips-score pieces into a single selector so consumers get exactly one
// emission per state change. Selecting them separately and joining with combineLatest fires
// once per individual selector re-emission (up to 4x for one data load), since each is an
// independent subscription to the store even though they update together in one reducer call.
export const selectMipsScoreBundle = createSelector(
  selectMipsScores,
  selectMvpScores,
  selectTopTraditionalMeasures,
  selectTopMvpMeasures,
  (mipsScores, mvpScores, topTraditionalMeasures, topMvpMeasures) => ({
    mipsScores,
    mvpScores,
    topTraditionalMeasures,
    topMvpMeasures,
  })
);
